import { create } from 'zustand';
import { CLUSTER_MAX_PX, CLUSTER_SIZE_EXPONENT } from '../utils/cluster-layout';

/**
 * Preview-only cluster knobs (panel opt-in with ?cluster_tune=1). Read from
 * ct_merge / ct_max / ct_breakout on load so a tuned link can be shared.
 * ponytail: URL params, not remote config; bake the winners in as defaults.
 */
export const CLUSTER_TUNING_DEFAULTS = {
  /** Merge clusters closer than this on screen (px), on top of overlap */
  mergePx: 160,
  /** Biggest bubble (px), still capped by the spacing between clusters */
  maxPx: CLUSTER_MAX_PX,
  /** Size contrast between small and big clusters (power-scale exponent) */
  sizeExp: CLUSTER_SIZE_EXPONENT,
  /** Show individual pins once this few chargers are in view */
  breakout: 60,
};
export type ClusterTuning = typeof CLUSTER_TUNING_DEFAULTS;
export const CLUSTER_TUNING_PARAMS: Record<keyof ClusterTuning, string> = {
  mergePx: 'ct_merge',
  maxPx: 'ct_max',
  breakout: 'ct_breakout',
  sizeExp: 'ct_contrast',
};

function fromUrl(): ClusterTuning {
  const q = new URLSearchParams(window.location.search);
  const out = { ...CLUSTER_TUNING_DEFAULTS };
  for (const key of Object.keys(out) as (keyof ClusterTuning)[]) {
    const v = Number(q.get(CLUSTER_TUNING_PARAMS[key]));
    if (q.has(CLUSTER_TUNING_PARAMS[key]) && Number.isFinite(v)) out[key] = v;
  }
  return out;
}

interface ClusterTuningState extends ClusterTuning {
  /** Clusters from the backend vs drawn after merging, for the panel */
  stats: { raw: number; shown: number };
  set: (patch: Partial<ClusterTuning>) => void;
  setStats: (stats: { raw: number; shown: number }) => void;
}

export const useClusterTuningStore = create<ClusterTuningState>((set) => ({
  ...fromUrl(),
  stats: { raw: 0, shown: 0 },
  set: (patch) => set(patch),
  setStats: (stats) => set({ stats }),
}));
